import { useEffect, useRef, useState } from "react";
import { SettingsIcon } from "../components/Icons";
import { useVideoPlayer2 } from "../hooks/use-video-player-2-setup-start";
import { useVideoPlayer2Qualities } from "../hooks/use-video-player-helpers";

export function SettingsButton() {
  const { currentQualityLevel, setQuality, qualityLevels } =
    useVideoPlayer2Qualities();
  const [open, setOpen] = useState(false);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const player = useVideoPlayer2();

  // Common speeds
  const speeds = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        !btnRef.current?.contains(e.target as Node) &&
        !menuRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
        setQualityOpen(false);
        setSpeedOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        className="video-player-2-btn video-player-2-settings"
        onClick={() => setOpen(o => !o)}
        title={player.trans("settings")}
        aria-label={player.trans("settings")}>
        <SettingsIcon />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="video-player-2-settings-menu"
          role="menu"
          aria-label={player.trans("settings") + " menu"}>
          {/* Quality Dropdown */}
          <div style={{ marginBottom: 0 }}>
            <button
              className="video-player-2-settings-btn"
              onClick={() => {
                setQualityOpen(q => {
                  if (!q) setSpeedOpen(false);
                  return !q;
                });
              }}
              aria-haspopup="true"
              aria-expanded={qualityOpen}
              role="menuitem">
              {player.trans("quality")} ({currentQualityLevel.label})
            </button>
            {qualityOpen && (
              <div
                style={{ background: "#333", marginTop: 4, borderRadius: 10 }}>
                {qualityLevels.map(level => (
                  <div
                    key={level.id}
                    className={
                      "video-player-2-quality-level" +
                      (currentQualityLevel?.id === level.id ? " selected" : "")
                    }
                    style={{}}
                    tabIndex={0}
                    role="menuitemradio"
                    aria-checked={currentQualityLevel?.id === level.id}
                    onClick={() => {
                      setQuality(level);
                      setQualityOpen(false);
                      setOpen(false);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        setQuality(level);
                        setQualityOpen(false);
                        setOpen(false);
                      }
                    }}>
                    {level.label}
                    {currentQualityLevel?.id === level.id && (
                      <span
                        style={{
                          marginLeft: "auto",
                          color: "#fff",
                          fontWeight: 700,
                        }}
                        aria-hidden="true">
                        ✔
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <hr className="video-player-2-settings-divider" />
          {/* Speed Dropdown */}
          <div>
            <button
              className="video-player-2-settings-btn"
              onClick={() => {
                setSpeedOpen(s => {
                  if (!s) setQualityOpen(false);
                  return !s;
                });
              }}
              style={{ width: "100%", textAlign: "left" }}
              aria-haspopup="true"
              aria-expanded={speedOpen}
              role="menuitem">
              {player.trans("speed")} ({player.speed}x)
            </button>
            {speedOpen && (
              <div
                style={{ background: "#333", marginTop: 4, borderRadius: 10 }}>
                {speeds.map(s => (
                  <div
                    key={s}
                    className={
                      "video-player-2-speed-level" +
                      (player.speed === s ? " selected" : "")
                    }
                    tabIndex={0}
                    role="menuitemradio"
                    aria-checked={player.speed === s}
                    onClick={() => {
                      player.setSpeed(s);
                      setSpeedOpen(false);
                      setOpen(false);
                    }}
                    onKeyDown={e => {
                      if (e.key === "Enter" || e.key === " ") {
                        player.setSpeed(s);
                        setSpeedOpen(false);
                        setOpen(false);
                      }
                    }}>
                    {s}x
                    {player.speed === s && (
                      <span
                        style={{
                          marginLeft: "auto",
                          color: "#fff",
                          fontWeight: 700,
                        }}
                        aria-hidden="true">
                        ✔
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
